export function isPlainObject(testObj: Record<string, any>): boolean {
  return Object.prototype.toString.call(testObj).indexOf('Object') !== -1
}

type KeyTransformer = (input: string, key: string, value: any) => string
type ValueTransformer<T> = (input: T, key: string, value: T) => T

function transformKey(
  input: string,
  key: string,
  value: any,
  transformers: Array<KeyTransformer>
) {
  return transformers.reduce((out, fn) => fn(out, key, value), input)
}

function transformValue<V>(
  input: V,
  key: string,
  value: V,
  transformers: ValueTransformer<V>[]
) {
  return transformers.reduce((out, fn) => fn(out, key, value), input)
}

/**
 * Remove or transform desired keys and null|undefined values from an object; optionally recursive
 *
 **/
type CleanObjKeysAndValuesOptions = {
  keysToRemove?: string[]
  keyTransformers?: KeyTransformer[]
  valueTransformers?: ValueTransformer<any>[]
  recursive?: boolean
}

export function cleanObjKeysAndValues<T = Record<string, unknown>, U = T>(
  obj: T,
  options: CleanObjKeysAndValuesOptions = {}
): U {
  const {
    keysToRemove = [],
    keyTransformers = [(x) => x],
    valueTransformers = [(x) => x],
    recursive = false
  } = options
  return Object.fromEntries(
    Object.entries(obj ?? {})
      .map(([key, value]) => {
        if (keysToRemove.includes(key)) {
          return undefined
        }

        const nextVal = transformValue(value, key, value, valueTransformers)
        if (nextVal == null) {
          return undefined
        }

        return [
          transformKey(key, key, nextVal, keyTransformers),
          recursive && isPlainObject(nextVal)
            ? cleanObjKeysAndValues(nextVal, {
                keysToRemove,
                keyTransformers,
                valueTransformers,
                recursive
              })
            : nextVal
        ]
      })
      .filter(Boolean)
  )
}

export const hasKey = (obj: Record<string, any>, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(obj, key)

export const omit = (
  object: Record<string, any> = {},
  paths: string | string[]
): Record<string, any> => {
  const omitKeys =
    typeof paths === 'string' ? new Set([paths]) : new Set((paths ?? []).flat())

  return Object.fromEntries(
    Object.entries(object).filter(([key]) => !omitKeys.has(key))
  )
}

export const pick = <T, K extends keyof T>(
  object: T,
  ...keysToExtract: K[]
): Pick<T, K> => {
  const extractKeys: Array<K> = [...new Set(keysToExtract ?? [])]
  return Object.fromEntries(
    extractKeys.map((key) => [key, object[key]])
  ) as Pick<T, K>
}
